import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/components';
import {
  useGetAreaCodes,
  useGetCardTypes,
  useGetCities,
  useGetRegions,
  useUploadFile,
} from '@xhype/card-app-sdk';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateUserCardHolder } from '@package/smart-wallet/hooks';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const dateField = z
  .string()
  .regex(DATE_REGEX, { message: 'Must be in yyyy-MM-dd format' });

/* Schemas */
export const RegisterFullDataFormSchema = z
  .object({
    cardTypeId: z.string().min(1, { message: 'Card Type is required' }),
    cardHolderModel: z.enum(['B2B', 'B2C']),
    firstName: z.string().min(1, { message: 'First Name is required' }),
    lastName: z.string().min(1, { message: 'Last Name is required' }),
    areaCode: z.string().min(1, { message: 'Area Code is required' }),
    mobile: z
      .string()
      .min(1, { message: 'Mobile number is required' })
      .refine((value) => /^\d+$/.test(value), {
        message: 'Invalid mobile number',
      }),
    address: z.string().min(1, { message: 'Address is required' }),
    birthday: z
      .string()
      .min(1, { message: 'Birthday is required' })
      .regex(DATE_REGEX, { message: 'Must be in yyyy-MM-dd format' }),
    town: z.string().min(1, { message: 'Town is required' }),
    postCode: z.string().min(1, { message: 'Post Code is required' }),
    // B2C identity fields
    idType: z
      .enum(['PASSPORT', 'HK_HKID', 'DLN', 'GOVERNMENT_ISSUED_ID_CARD'])
      .optional(),
    idNumber: z.string().min(2).max(50).optional(),
    issueDate: dateField.optional(),
    idNoExpiryDate: dateField.optional(),
    idFrontId: z.string().optional(),
    idBackId: z.string().optional(),
    idHoldId: z.string().optional(),
    // B2C optional personal fields
    nationality: z.string().length(2).optional(),
    gender: z.enum(['M', 'F']).optional(),
    occupation: z.string().optional(),
    annualSalary: z.string().optional(),
    accountPurpose: z.string().optional(),
    expectedMonthlyVolume: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.cardHolderModel !== 'B2C') return;
    const b2cRequired: (keyof typeof data)[] = [
      'idType',
      'idNumber',
      'issueDate',
      'idNoExpiryDate',
      'idFrontId',
      'idBackId',
      'idHoldId',
    ];
    for (const field of b2cRequired) {
      if (!data[field]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: 'Required for B2C',
        });
      }
    }
  });

/* Types */
export type RegisterFullDataFormType = z.infer<
  typeof RegisterFullDataFormSchema
>;

/* Internal component — file upload field */
const FileUploadField = ({
  label,
  onUploaded,
  error,
}: {
  label: string;
  onUploaded: (fileId: string) => void;
  error?: string;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);

  const uploadFile = useUploadFile({
    onSuccess: (data) => {
      onUploaded(data.data.fileId);
      setUploaded(true);
      setUploading(false);
    },
    onError: () => {
      setUploading(false);
    },
  });

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploaded(false);
    await uploadFile.mutateAsync({ file });
  };

  return (
    <div className='flex flex-col gap-1'>
      <span className='text-sm font-medium'>{label}</span>
      <div className='flex items-center gap-2'>
        <input
          ref={inputRef}
          type='file'
          accept='image/jpeg,image/png'
          className='hidden'
          onChange={handleChange}
        />
        <Button
          type='button'
          variant='outline'
          className='cursor-pointer'
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? 'Uploading...' : uploaded ? 'Replace' : 'Choose file'}
        </Button>
        {uploaded && (
          <span className='text-sm text-green-600'>Uploaded ✓</span>
        )}
      </div>
      {error && <p className='text-sm text-destructive'>{error}</p>}
    </div>
  );
};

/* Internal components */
const CreateCardHolderForm = () => {
  const [regionCode, setRegionCode] = useState('');
  const { register } = useCreateUserCardHolder({});
  const form = useForm<RegisterFullDataFormType>({
    resolver: zodResolver(RegisterFullDataFormSchema),
    defaultValues: { cardHolderModel: 'B2B' },
  });

  const cardHolderModel = form.watch('cardHolderModel');
  const isB2C = cardHolderModel === 'B2C';

  /* Queries */
  const { data: regions } = useGetRegions({
    onError: (error: Error) => {
      console.error(`Get Regions: Axios error occurred - ${error.message}`);
    },
  });

  const { data: cities } = useGetCities({
    regionCode,
    enabled: regionCode !== '',
    onError: (error: Error) => {
      console.error(`Get Cities: Axios error occurred - ${error.message}`);
    },
  });

  const { data: areaCodes } = useGetAreaCodes({
    onError: (error: Error) => {
      console.error(`Get Area Codes: Axios error occurred - ${error.message}`);
    },
  });

  const { data: cardTypes } = useGetCardTypes({
    onError: (error: Error) => {
      console.error(`Get Card Type: Axios error occurred - ${error.message}`);
    },
  });

  /* Callbacks */
  const onSubmit = useCallback(
    async (data: RegisterFullDataFormType) => {
      try {
        await register({
          ...data,
          country: regionCode,
        });
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [register, regionCode],
  );

  /* Memos */
  const countries = useMemo(
    () => regions?.map((region) => region.code) || [],
    [regions],
  );

  const mobileAreaCodes = useMemo(
    () => areaCodes?.map((areaCode) => areaCode.code) || [],
    [areaCodes],
  );

  const cardTypesId = useMemo(
    () => cardTypes?.map((cardType) => cardType.cardTypeId.toString()) || [],
    [cardTypes],
  );

  const towns = useMemo(() => cities?.map((city) => city.code) || [], [cities]);

  /* Render */
  return (
    <section className='w-full flex flex-col gap-4'>
      {/* Country selector (outside form due to dependent city fetch) */}
      <div className='flex flex-col gap-2'>
        <span className='text-sm font-medium'>Country</span>
        <select
          className='border rounded p-2'
          onChange={(e) => setRegionCode(e.target.value)}
        >
          <option value=''>Select Country</option>
          {countries.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      {regionCode !== '' && (
        <Form {...form}>
          <form
            className='w-full flex flex-col gap-2'
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {/* Card Holder Model toggle */}
            <FormField
              control={form.control}
              name='cardHolderModel'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Card Holder Model</FormLabel>
                  <FormControl>
                    <select {...field} className='border rounded p-2'>
                      <option value='B2B'>B2B (Business)</option>
                      <option value='B2C'>B2C (Individual / Physical card)</option>
                    </select>
                  </FormControl>
                  <FormMessage className='text-start' />
                </FormItem>
              )}
            />

            {/* Card Type */}
            <FormField
              control={form.control}
              name='cardTypeId'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Card Type</FormLabel>
                  <FormControl>
                    <select {...field} className='border rounded p-2'>
                      <option value=''>Select Card Type</option>
                      {cardTypesId.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage className='text-start' />
                </FormItem>
              )}
            />

            {/* Common fields */}
            {(
              [
                { name: 'firstName', label: 'First Name' },
                { name: 'lastName', label: 'Last Name' },
                { name: 'birthday', label: 'Birthday (YYYY-MM-DD)' },
                { name: 'mobile', label: 'Mobile' },
              ] as const
            ).map(({ name, label }) => (
              <FormField
                key={name}
                control={form.control}
                name={name}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{label}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={label} />
                    </FormControl>
                    <FormMessage className='text-start' />
                  </FormItem>
                )}
              />
            ))}

            {/* Area Code */}
            <FormField
              control={form.control}
              name='areaCode'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Area Code</FormLabel>
                  <FormControl>
                    <select {...field} className='border rounded p-2'>
                      <option value=''>Select Area Code</option>
                      {mobileAreaCodes.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage className='text-start' />
                </FormItem>
              )}
            />

            {/* Town */}
            <FormField
              control={form.control}
              name='town'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Town</FormLabel>
                  <FormControl>
                    <select {...field} className='border rounded p-2'>
                      <option value=''>Select Town</option>
                      {towns.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage className='text-start' />
                </FormItem>
              )}
            />

            {/* Address & PostCode */}
            {(
              [
                { name: 'address', label: 'Address' },
                { name: 'postCode', label: 'Post Code' },
              ] as const
            ).map(({ name, label }) => (
              <FormField
                key={name}
                control={form.control}
                name={name}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{label}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={label} />
                    </FormControl>
                    <FormMessage className='text-start' />
                  </FormItem>
                )}
              />
            ))}

            {/* ── B2C fields ──────────────────────────────────────────── */}
            {isB2C && (
              <>
                {/* ID Type */}
                <FormField
                  control={form.control}
                  name='idType'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ID Type *</FormLabel>
                      <FormControl>
                        <select {...field} className='border rounded p-2'>
                          <option value=''>Select ID Type</option>
                          <option value='PASSPORT'>Passport</option>
                          <option value='HK_HKID'>HK Identity Card</option>
                          <option value='DLN'>Driver&apos;s License</option>
                          <option value='GOVERNMENT_ISSUED_ID_CARD'>
                            Government-issued ID
                          </option>
                        </select>
                      </FormControl>
                      <FormMessage className='text-start' />
                    </FormItem>
                  )}
                />

                {/* ID Number, Issue Date, Expiry Date */}
                {(
                  [
                    { name: 'idNumber', label: 'ID Number *' },
                    { name: 'issueDate', label: 'Issue Date (YYYY-MM-DD) *' },
                    {
                      name: 'idNoExpiryDate',
                      label: 'Expiry Date (YYYY-MM-DD) *',
                    },
                  ] as const
                ).map(({ name, label }) => (
                  <FormField
                    key={name}
                    control={form.control}
                    name={name}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{label}</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder={label} />
                        </FormControl>
                        <FormMessage className='text-start' />
                      </FormItem>
                    )}
                  />
                ))}

                {/* File uploads */}
                <FileUploadField
                  label='ID Front Photo *'
                  onUploaded={(id) => form.setValue('idFrontId', id)}
                  error={form.formState.errors.idFrontId?.message}
                />
                <FileUploadField
                  label='ID Back Photo * (reuse front if no back)'
                  onUploaded={(id) => form.setValue('idBackId', id)}
                  error={form.formState.errors.idBackId?.message}
                />
                <FileUploadField
                  label='Selfie / Face Photo *'
                  onUploaded={(id) => form.setValue('idHoldId', id)}
                  error={form.formState.errors.idHoldId?.message}
                />
              </>
            )}

            <Button
              className='w-full cursor-pointer uppercase'
              type='submit'
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? 'Registering...' : 'Register'}
            </Button>
          </form>
        </Form>
      )}
    </section>
  );
};

/* Main component */
export const CreateCardHolder = () => {
  return (
    <div className='h-full w-full flex flex-col items-center justify-center'>
      <section className='w-2/3 flex flex-col items-center justify-center gap-4'>
        <CreateCardHolderForm />
      </section>
    </div>
  );
};
